/**
 * AssignInserviceDialog — assign in-service trainings (from the Onboarding Kit library) to one or
 * many existing employees via a training-only link. No background-check gate.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Check, Copy, ExternalLink, FileText, GraduationCap, Loader2, Video } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AssignInserviceDialog({ open, onClose, employees, preselected = [], onAssigned }) {
  const [library, setLibrary] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const [who, setWho] = useState(new Set(preselected));
  const [dueDays, setDueDays] = useState("30");
  const [sendEmail, setSendEmail] = useState(true);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const multi = employees.length > 1;

  useEffect(() => {
    if (!open) return;
    setPicked(new Set()); setWho(new Set(preselected)); setResult(null); setSearch("");
    axios.get(`${API}/settings/orientation-kit`).then((r) => setLibrary(r.data.inservices || [])).catch(() => setLibrary([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggle = (set, id) => { const n = new Set(set); n.has(id) ? n.delete(id) : n.add(id); return n; };
  const visibleEmployees = useMemo(() => employees.filter((e) => (e.name || "").toLowerCase().includes(search.toLowerCase())), [employees, search]);
  const totalHours = useMemo(() => (library || []).filter((i) => picked.has(i.id)).reduce((a, i) => a + (Number(i.hours) || 0), 0), [library, picked]);

  const assign = async () => {
    if (picked.size === 0) return toast.error("Pick at least one in-service");
    if (who.size === 0) return toast.error("Pick at least one employee");
    setSaving(true);
    const expires_at = dueDays && Number(dueDays) > 0 ? new Date(Date.now() + Number(dueDays) * 86400000).toISOString() : null;
    try {
      const ids = [...who];
      const body = { inservice_ids: [...picked], expires_at, send_email: sendEmail };
      const res = ids.length === 1
        ? { data: { created: [(await axios.post(`${API}/employees/${ids[0]}/inservice-link`, body)).data] } }
        : await axios.post(`${API}/training-hours/assign`, { ...body, employee_ids: ids });
      const created = res.data.created;
      setResult(created);
      toast.success(`Assigned to ${created.length} employee${created.length > 1 ? "s" : ""}${sendEmail ? ` · ${created.filter((c) => c.email_sent).length} emailed` : ""}`);
      onAssigned?.(created);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not assign");
    } finally { setSaving(false); }
  };

  const copy = async (c) => { await navigator.clipboard.writeText(c.full_url); toast.success("Link copied"); };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="assign-inservice-dialog" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><GraduationCap className="w-5 h-5 text-teal-600" /> Assign in-service training</DialogTitle>
        </DialogHeader>

        {result ? (
          <div className="space-y-3" data-testid="assign-inservice-result">
            <p className="text-sm text-slate-600">Training links created. Employees can open them any time until the due date.</p>
            <ul className="divide-y divide-slate-200 rounded-md border border-slate-200">
              {result.map((c) => (
                <li key={c.id} className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm" data-testid={`assign-result-${c.employee_id}`}>
                  <Check className="w-4 h-4 text-emerald-600" />
                  <span className="font-medium">{c.employee_name}</span>
                  <span className="text-xs text-slate-500">{c.inservice_titles.join(", ")}{c.email_sent ? " · emailed" : ""}</span>
                  <span className="ml-auto flex gap-1">
                    <Button variant="ghost" size="sm" className="h-7 gap-1 text-xs" onClick={() => copy(c)} data-testid={`assign-copy-${c.employee_id}`}><Copy className="w-3 h-3" /> Copy link</Button>
                    <Button variant="ghost" size="sm" className="h-7 gap-1 text-xs" onClick={() => window.open(c.full_url, "_blank")}><ExternalLink className="w-3 h-3" /> Open</Button>
                  </span>
                </li>
              ))}
            </ul>
            <DialogFooter><Button onClick={onClose} data-testid="assign-inservice-done">Done</Button></DialogFooter>
          </div>
        ) : (
          <div className="space-y-5">
            {multi && (
              <section>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-xs">Employees ({who.size} selected)</Label>
                  <button type="button" className="text-[11px] text-teal-700 hover:underline" onClick={() => setWho(who.size === employees.length ? new Set() : new Set(employees.map((e) => e.id)))} data-testid="assign-employees-toggle-all">
                    {who.size === employees.length ? "Clear all" : "Select all"}
                  </button>
                </div>
                <Input placeholder="Search employees…" value={search} onChange={(e) => setSearch(e.target.value)} className="mb-2 h-8" data-testid="assign-employee-search" />
                <div className="grid sm:grid-cols-2 gap-1 max-h-40 overflow-y-auto rounded-md border border-slate-200 p-2">
                  {visibleEmployees.map((e) => (
                    <label key={e.id} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="checkbox" checked={who.has(e.id)} onChange={() => setWho((s) => toggle(s, e.id))} data-testid={`assign-employee-${e.id}`} />
                      <span className="truncate">{e.name}</span>{e.role && <span className="text-[10px] text-slate-400 uppercase">{e.role}</span>}
                    </label>
                  ))}
                </div>
              </section>
            )}
            {!multi && <p className="text-sm text-slate-600">For <span className="font-medium text-slate-900">{employees[0]?.name}</span></p>}

            <section>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs">In-services ({picked.size} selected{totalHours ? ` · ${totalHours} h` : ""})</Label>
                {library?.length > 0 && (
                  <button type="button" className="text-[11px] text-teal-700 hover:underline" onClick={() => setPicked(picked.size === library.length ? new Set() : new Set(library.map((i) => i.id)))} data-testid="assign-inservices-toggle-all">
                    {picked.size === library.length ? "Clear all" : "Select all"}
                  </button>
                )}
              </div>
              {library === null ? <Loader2 className="w-4 h-4 animate-spin text-slate-400" /> : library.length === 0 ? (
                <p className="text-sm text-slate-500 italic">No in-services in the library yet — add them under Settings → Onboarding Kit → In-service trainings.</p>
              ) : (
                <div className="space-y-1 rounded-md border border-teal-200 bg-teal-50/40 p-2 max-h-56 overflow-y-auto">
                  {library.map((i) => (
                    <label key={i.id} className="flex items-center gap-2 text-sm cursor-pointer rounded px-1 py-0.5 hover:bg-white">
                      <input type="checkbox" checked={picked.has(i.id)} onChange={() => setPicked((s) => toggle(s, i.id))} data-testid={`assign-inservice-${i.id}`} />
                      <span className="truncate">{i.title}</span>
                      {i.hours ? <span className="text-[10px] text-slate-400">{i.hours} h</span> : null}
                      {i.source_type !== "pdf" && <Video className="w-3 h-3 text-slate-400" />}
                      {i.pdf_storage_path && <span className="inline-flex items-center gap-0.5 text-[10px] rounded bg-purple-100 text-purple-700 px-1"><FileText className="w-3 h-3" /> PDF</span>}
                    </label>
                  ))}
                </div>
              )}
            </section>

            <div className="grid sm:grid-cols-2 gap-4 items-end">
              <div>
                <Label className="text-xs">Due in (days, blank = no deadline)</Label>
                <Input type="number" min="1" value={dueDays} onChange={(e) => setDueDays(e.target.value)} data-testid="assign-due-days" />
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700 pb-2">
                <Switch checked={sendEmail} onCheckedChange={setSendEmail} data-testid="assign-send-email" /> Email the link to each employee
              </label>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button onClick={assign} disabled={saving || !library?.length} className="gap-1 bg-teal-600 hover:bg-teal-700" data-testid="assign-inservice-submit">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <GraduationCap className="w-4 h-4" />} Assign{who.size > 1 ? ` to ${who.size}` : ""}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
